import React, {
	useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { matchPath, useHistory, useRouteMatch } from 'react-router-dom';
import {
	message, Spin, Modal, Button,
	notification,
} from 'antd';
import {
	CheckOutlined, LeftCircleFilled, LoadingOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { debounce, isElement } from 'lodash';
import './app.scoped.css';
import { MainScreen } from './screens';
import {
	ProctorState, useAiProctorStore, useAppStore, useQuizStore, useSessionStore,
} from './store';
import { logger } from './libs/utils/logger';

import { ReactComponent as Loader } from './assets/icons/loader.svg';
import { InvigilatorCam } from './components/invigilator-cam';
import {
	CheatingType, TabSwitchEventType, QuestionType, WebcamStateKind, APP_CONFIG, FullScreenEventType,
	isElectronApp, ScreenShareState, FileUploadEvent, localStorageKeyForVmDetection,
} from './config';
import { AIProctor } from './libs/object-dection/object-detection';
import { RemoteProctoring } from './components/remote-proctoring';
import { ErrorBoundary } from './components/remote-proctoring/error-boundary';
import { createWatermark } from './libs/utils/canvas';
import { useDevice } from './hooks/use-device';
import { MainMobileScreen } from './mobile-view/main-mobile-screen';
import { setOpenReplaySessionContext } from './libs/utils/openreplay';

const aiProctorSelector = (state: ProctorState) => ({
	noUserPresent: state.noUserPresent,
	multipleUserFound: state.multipleUserFound,
	aiProctor: state.aiProctoring,
	setAiProctoring: state.setAiProctoring,
	startProctoring: state.startProctoring,
});

const appStoreSelector = (state: AppState) => ({
	loading: state.loading,
	loadingText: state.loadingText,
	openInApp: state.openInApp,
	appModalShown: state.appModalShown,
	screenShareState: state.screenShareState,
	videoElement: state.videoElement,

	setVideoElement: state.setVideoElement,
	setAppLoading: state.setAppLoading,
	openLinkInApp: state.openLinkInApp,
	setAppModalShown: state.setAppModalShown,
	setScreenShareState: state.setScreenShareState,
});

const sessionStoreSelector = (state: SessionState) => ({
	isAuthenticated: state.isAuthenticated,
	sessionData: state.sessionData,
	getSessionData: state.getSessionData,
	connectSocket: state.connectSocket,
});

const quizStoreSelector = (state: QuizState) => ({
	quizData: state.quizData,
	attemptQuestionData: state.attemptQuestionData,
	hasStarted: state.hasStarted,
	timeLeft: state.timeLeft,
	serverTime: state.serverTime,
	uploadingAllRecording: state.uploadingAllRecording,
	liveStreamRoomName: state.liveStreamRoomName,
	screenShareStream: state.screenShareStream,

	setScreenShareStream: state.setScreenShareStream,
	userRemovedFromRoom: state.userRemovedFromRoom,
	setQuizTimeLeft: state.setQuizTimeLeft,
	submitQuiz: state.submitQuiz,
	getQuizData: state.getQuizData,
	getQuizDashboardData: state.getQuizDashboardData,
	sendImageData: state.sendImageData,
	sendCheatingDetails: state.sendCheatingDetails,
	getServerTime: state.getServerTime,
	incrementByOneSecServerTime: state.incrementByOneSecServerTime,
	setTimeLeft: state.setQuizTimeLeft,
	incrementLocalTabCount: state.incrementLocalTabCount,
	getLiveStreamRoom: state.getLiveStreamRoom,
	uploadEvent: state.uploadEvent,
	uploadEventError: state.uploadErrorEvent,
	stopRecording: state.stopRecording,
	startRecording: state.startRecording,
	validateAppKey: state.validateKey,
	checkForVM: state.checkForVM,
	sendContentProtection: state.sendContentProtection,
	sendRemoteActionResponse: state.sendRemoteActionResponse,
});

export default function App(): JSX.Element {
	const history = useHistory();
	const routeMatch = useRouteMatch();
	const { isMobile } = useDevice();
	const {
		loading, loadingText, setAppLoading, openInApp, openLinkInApp, setAppModalShown,
		appModalShown, screenShareState, setScreenShareState, videoElement, setVideoElement,
	} = useAppStore(appStoreSelector);

	const {
		noUserPresent, setAiProctoring, aiProctor, startProctoring,
	} = useAiProctorStore(aiProctorSelector);

	const {
		isAuthenticated, sessionData, getSessionData, connectSocket,
	} = useSessionStore(sessionStoreSelector);
	const {
		quizData, attemptQuestionData, hasStarted, timeLeft, uploadingAllRecording,
		liveStreamRoomName, screenShareStream,
		setQuizTimeLeft, getQuizData, setScreenShareStream,
		getQuizDashboardData, sendImageData, sendCheatingDetails, submitQuiz, setTimeLeft,
		getServerTime, incrementByOneSecServerTime, incrementLocalTabCount, getLiveStreamRoom,
		serverTime, uploadEvent, uploadEventError, startRecording, stopRecording, userRemovedFromRoom,
		validateAppKey, checkForVM, sendContentProtection, sendRemoteActionResponse,
	} = useQuizStore(quizStoreSelector);

	const enableCamera = useMemo(() => quizData?.isWebCamAllowed, [
		quizData?.isWebCamAllowed,
	]);

	const historyOnPopState = useCallback(() => {
		history.go(1);
	}, [history]);

	const quizMatchParams = useMemo(() => matchPath<{ quizName: string }>(history.location.pathname, {
		path: '/test/:quizName',
		strict: true,
	})?.params, [history]);

	const [invigilatorCamStream, setInvigilatorCamStream] = useState<MediaStream | null>(null);
	const [jitsiWebCanStream, setJitsiWebCamStream] = useState<MediaStreamTrack | null>(null);
	const [jitsiAudioTrack, setJitsiAudioTrack] = useState<MediaStreamTrack | null>(null);
	const [isMicInUse, setIsMicInUse] = useState<boolean>(false);
	const [isAppIntegrigityChecked, setAppIntegrigityChecked] = useState<boolean>(false);

	const [webcamState, setWebcamState] = useState<number>(
		enableCamera ? WebcamStateKind.Loading : WebcamStateKind.Disabled,
	);
	const [fullScreenModalVisible, setFullScreenModalVisible] = useState<boolean>(false);
	const [isSubjectiveFileQuestion, setIsSubjectiveFileQuestion] = useState<boolean>(false);
	const [appErrorModalVisible, setErrorModalVisible] = useState<boolean>(false);
	const [loadingMultipleScreenRetry, setLoadginMultipleScreenRetry] = useState<boolean>(false);
	// const [jaasRoom, setJaasRoom] = useState<any>(null);
	const [multipleCamera, setMultipleCamera] = useState<boolean>(false);
	const [webCamList, setWebCamList] = useState<Array<string>>([]);
	const tabSwitchAlertCount = useRef<number>(0);
	const [showVMMessage, setShowVMMessage] = useState<boolean>(false);
	const [errorWhileUploading, setErrorWhileUploading] = useState<boolean>(false);
	const [initialLoadComplete, setInitialComplete] = useState<boolean>(false);
	const [watermarkURL, setWatermarkUrl] = useState<string | undefined>();

	const loadingMediaStream = useRef<boolean>(false);

	useEffect(() => {
		if (!quizData?.isWatermarkEnabled) {
			return;
		}
		if (watermarkURL === undefined) {
			return;
		}
		document.documentElement.style.setProperty('--watermark-background-image', `url('${watermarkURL}')`);
	}, [watermarkURL, quizData?.isWatermarkEnabled]);

	useEffect(() => {
		if (!isAuthenticated || !quizData?.isWatermarkEnabled) {
			return;
		}
		let customWatermark;
		if (quizData.isCustomWatermarkEnabled) {
			customWatermark = quizData?.customWatermark;
		}
		const width = 200;
		const height = 100;
		const color = '#0000003F';
		const text = customWatermark || sessionData?.enrollmentId || sessionData?.email || '';
		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;
		const context = canvas.getContext('2d');
		if (context === null) {
			return;
		}
		createWatermark(context, text, width, height, color);
		canvas.toBlob((blob) => {
			if (blob === null) {
				return;
			}
			const url = URL.createObjectURL(blob);
			setWatermarkUrl((oldUrl) => {
				if (oldUrl !== undefined) {
					URL.revokeObjectURL(oldUrl);
				}
				return url;
			});
		});
	// eslint-disable-next-line max-len
	}, [isAuthenticated, setWatermarkUrl, sessionData?.displayName, sessionData?.enrollmentId, sessionData?.email, quizData?.isWatermarkEnabled, quizData?.customWatermark, quizData?.isCustomWatermarkEnabled]);

	useEffect(() => {
		let aiProctoringToSent: AIProctor | null = null;
		if (quizData?.isAppOnly && !isElectronApp) {
			setAiProctoring(null);
			return () => { };
		}
		if (!quizData?.isWebCamAllowed) {
			return () => { };
		}
		if (videoElement && quizData?.isWebCamAllowed) {
			aiProctoringToSent = new AIProctor(videoElement, {
				frequency: 3000,
				webWorker: true,
			});
			setAiProctoring(aiProctoringToSent);
		}
		return () => {
			if (aiProctoringToSent) {
				setAiProctoring(null);
			}
		};
	}, [
		videoElement,
		quizData?.isAppOnly,
		quizData?.isWebCamAllowed,
		setAiProctoring,
	]);

	useEffect(() => {
		if (aiProctor && sessionData?.userId && quizData?.quizId) {
			aiProctor.setUploadConfig({
				presignedUrlEndpoint: `${APP_CONFIG.StaticStorageApiURL}/getPresignedURL`,
				userId: sessionData.userId,
				quizId: quizData.quizId,
			});
		}
	}, [aiProctor, sessionData?.userId, quizData?.quizId]);

	useEffect(() => {
		if (videoElement && aiProctor && sessionData?.userId) {
			startProctoring();
		}
	}, [videoElement, aiProctor, sessionData?.userId, startProctoring]);

	useEffect(() => {
		if (isElectronApp && 'api' in window) {
			(window.api as any)._receiveFromMain('electron-strict-mode-lock', (data: boolean) => {
				if (APP_CONFIG.DisabledMultiScreenCheck) {
					return;
				}
				setErrorModalVisible(false);
			});
		}
	}, [quizData?.allowClose, setErrorModalVisible]);

	useEffect(() => {
		if (isElectronApp && 'api' in window) {
			(window.api as any)._receiveFromMain('electron-strict-mode-lock', (data: boolean) => {
				setErrorModalVisible(data);
			});
		}
	}, [quizData?.allowClose, setErrorModalVisible]);

	const refreshMonitorCheck = useCallback(async () => {
		setLoadginMultipleScreenRetry(true);
		if ('api' in window) {
			await (window.api as any)._sendToMain('_retryMultiScreen', true);
		}
		setLoadginMultipleScreenRetry(false);
	}, []);

	const sendTabSwitchNotification = useCallback(() => {
		logger.info('switching tabs...!');
		incrementLocalTabCount();
		if (hasStarted && !(!isElectronApp && quizData?.isAppOnly && quizData.isPrivate)
			&& !(attemptQuestionData?.type
				&& (+attemptQuestionData?.type
					=== QuestionType.Subjective || +attemptQuestionData?.type === QuestionType.Web))) {
			if (tabSwitchAlertCount.current < 3) {
				message.error('Tab switching not allowed!');

				tabSwitchAlertCount.current += 1;
			}
			console.log('Tab Switch Event');
			sendCheatingDetails(CheatingType.TabSwitch, TabSwitchEventType.Out);
		}
	}, [incrementLocalTabCount,
		hasStarted,
		quizData,
		attemptQuestionData?.type,
		sendCheatingDetails,
	]);

	const sendTabFocusNotification = debounce(useCallback(() => {
		logger.info('window focus....!', Date.now());
		if (hasStarted && !(!isElectronApp && quizData?.isAppOnly && quizData.isPrivate)
			&& !(attemptQuestionData?.type
				&& (+attemptQuestionData?.type
					=== QuestionType.Subjective || +attemptQuestionData?.type === QuestionType.Web))) {
			if (tabSwitchAlertCount.current < 3) {
				message.error('Tab switching not allowed!');

				tabSwitchAlertCount.current += 1;
			}
			sendCheatingDetails(CheatingType.TabSwitch, TabSwitchEventType.In);
		}
	}, [attemptQuestionData?.type,
		hasStarted, quizData, sendCheatingDetails]), 100);

	const sendLockScreenNotification = debounce(useCallback((date: number | undefined) => {
		sendCheatingDetails(CheatingType.ScreenLock, TabSwitchEventType.Out, date);
	}, [sendCheatingDetails]));

	const sendUnlockScreenNotification = debounce(useCallback(() => {
		sendCheatingDetails(CheatingType.ScreenLock, TabSwitchEventType.In);
	}, [sendCheatingDetails]));

	const sendContentProtectionNotification = debounce(useCallback(
		(data: { payload: Record<string, unknown>; headers: Record<string, unknown> }) => {
			sendContentProtection(data);
		},
		[sendContentProtection],
	));

	const sendRemoteActionResponseWrapper = debounce(useCallback(
		(data: any) => {
			sendRemoteActionResponse(data);
		},
		[sendRemoteActionResponse],
	));

	const handleFocusEvent = useCallback((event: FocusEvent) => {
		if (event.target !== window) return;
		sendTabFocusNotification();
	}, [sendTabFocusNotification]);

	const handleBlurEvent = useCallback((event: FocusEvent) => {
		if (event.target !== window) return;
		sendTabSwitchNotification();
	}, [sendTabSwitchNotification]);

	const sendWebcamBlockNotification = useCallback(() => {
		logger.info('blocking webcam...!');

		sendCheatingDetails(CheatingType.WebcamBlock);
	}, [sendCheatingDetails]);

	const checkForMultipleCamera = useCallback(async () => {
		try {
			const devicesAvailable = await navigator.mediaDevices.enumerateDevices();
			let countOfVideoDevices = 0;
			const cameraList: Array<string> = [];
			(devicesAvailable ?? []).forEach((element) => {
				if (element.kind === 'videoinput') {
					countOfVideoDevices += 1;
					cameraList.push(element.label);
				}
			});
			setWebCamList(cameraList);
			console.log('User Devices', devicesAvailable);
			if (countOfVideoDevices > 1) {
				setMultipleCamera(true);
				return;
			}
			setMultipleCamera(false);
		} catch (error) {
			console.error(error);
		}
	}, [setMultipleCamera, setWebCamList]);

	const onStreamStop = useCallback(() => {
		setWebcamState(WebcamStateKind.Blocked);
		console.trace('Stream Stopped');
		let streamForMedia: MediaStream | null = null;
		sendWebcamBlockNotification();
		if ('api' in window) {
			const restartCamInterval = setInterval(async () => {
				try {
					setWebcamState(WebcamStateKind.Loading);
					streamForMedia = await navigator.mediaDevices.getUserMedia({
						video: true,
						audio: false,
						// audio: true,
					});
					setWebcamState(WebcamStateKind.Streaming);
					setJitsiWebCamStream(streamForMedia.getVideoTracks()?.[0] ?? null);
					// setJitsiAudioTrack(streamForMedia.getAudioTracks()?.[0] ?? null);
					streamForMedia.addEventListener('inactive', onStreamStop, false);
					clearInterval(restartCamInterval);
				} catch (error) {
					console.error(error);
				}
			}, 1000);
		}
		return () => {
			if (streamForMedia) {
				streamForMedia.removeEventListener('inactive', onStreamStop);
			}
		};
	}, [sendWebcamBlockNotification, setJitsiWebCamStream]);

	const handleScreenShareStreamStop = useCallback(() => {
		console.trace('Screen Share Stoped');
		setScreenShareState(ScreenShareState.Loading);
	}, [setScreenShareState]);

	useEffect(() => {
		if (quizData?.onlySuspiciousRoom) {
			console.log('Chage in only suspicious room');
		}
		if (quizData?.quizId && sessionData?.userId && quizData?.isLiveStreamEnabled) {
			getLiveStreamRoom();
		}
	}, [
		quizData?.quizId,
		quizData?.isLiveStreamEnabled,
		quizData?.onlySuspiciousRoom,
		sessionData?.userId,
		getLiveStreamRoom,
	]);

	useEffect(() => {
		if (quizData?.quizId && isElectronApp
			&& 'api' in window && typeof window.api === 'object'
			&& window.api && '_setQuizId' in window.api && window.api._setQuizId
			&& typeof window.api._setQuizId === 'function'
		) {
			window.api._setQuizId(quizData.quizId);
		}
	}, [quizData?.quizId]);

	useEffect(() => {
		if (screenShareStream) {
			screenShareStream.addEventListener('ended', handleScreenShareStreamStop);
		}
		return () => {
			if (screenShareStream) {
				screenShareStream.removeEventListener('ended', handleScreenShareStreamStop);
			}
		};
	}, [screenShareStream, handleScreenShareStreamStop]);

	// ------------------------ Cheating Event Handler ---------------------------------------------//
	// Event Handler For Electron

	useEffect(() => {
		if (!isAuthenticated || !quizData?.quizId) {
			return () => { };
		}
		if (
			isElectronApp && 'api' in window && window.api
			&& typeof window.api === 'object' && '_receiveFromMain' in window.api
			&& window.api._receiveFromMain && typeof window.api._receiveFromMain === 'function'
		) {
			window.api._receiveFromMain('lock-screen-event', sendLockScreenNotification);
		}
		return () => {
			if (
				isElectronApp && 'api' in window && window.api
				&& typeof window.api === 'object' && '_removeListener' in window.api
				&& window.api._removeListener && typeof window.api._removeListener === 'function'
			) {
				window.api._removeListener('lock-screen-event');
			}
		};
	}, [isAuthenticated, quizData?.quizId, sendLockScreenNotification]);

	useEffect(() => {
		if (!isAuthenticated || !quizData?.quizId) {
			return () => { };
		}
		if (
			isElectronApp && 'api' in window && window.api
			&& typeof window.api === 'object' && '_receiveFromMain' in window.api
			&& window.api._receiveFromMain && typeof window.api._receiveFromMain === 'function'
		) {
			window.api._receiveFromMain('unlock-screen-event', sendUnlockScreenNotification);
		}
		return () => {
			if (
				isElectronApp && 'api' in window && window.api
				&& typeof window.api === 'object' && '_removeListener' in window.api
				&& window.api._removeListener && typeof window.api._removeListener === 'function'
			) {
				window.api._removeListener('unlock-screen-event');
			}
		};
	}, [isAuthenticated, quizData?.quizId, sendUnlockScreenNotification]);

	useEffect(() => {
		if (!isAuthenticated || !quizData?.quizId) {
			return () => { };
		}
		if (
			isElectronApp && 'api' in window && window.api
			&& typeof window.api === 'object' && '_receiveFromMain' in window.api
			&& window.api._receiveFromMain && typeof window.api._receiveFromMain === 'function'
		) {
			window.api._receiveFromMain('content-protection-event', sendContentProtectionNotification);
		}
		return () => {
			if (
				isElectronApp && 'api' in window && window.api
				&& typeof window.api === 'object' && '_removeListener' in window.api
				&& window.api._removeListener && typeof window.api._removeListener === 'function'
			) {
				window.api._removeListener('content-protection-event');
			}
		};
	}, [isAuthenticated, quizData?.quizId, sendContentProtectionNotification]);

	useEffect(() => {
		if (!isAuthenticated) {
			return () => { };
		}
		if (
			isElectronApp && 'api' in window && window.api
			&& typeof window.api === 'object' && '_receiveFromMain' in window.api
			&& window.api._receiveFromMain && typeof window.api._receiveFromMain === 'function'
		) {
			window.api._receiveFromMain('remote-action-response', sendRemoteActionResponseWrapper);
		}
		return () => {
			if (
				isElectronApp && 'api' in window && window.api
				&& typeof window.api === 'object' && '_removeListener' in window.api
				&& window.api._removeListener && typeof window.api._removeListener === 'function'
			) {
				window.api._removeListener('remote-action-response');
			}
		};
	}, [isAuthenticated, sendRemoteActionResponseWrapper]);

	useEffect(() => {
		if (isElectronApp && 'api' in window && isAuthenticated) {
			(window.api as any)._sendToMain('_quiz-status', true);
		}
	}, [isAuthenticated]);

	useEffect(() => {
		if (sessionData?.userId) {
			if (isElectronApp && 'api' in window) {
				(window.api as any)._sendToMain('session', sessionData);
			}
		}
	}, [sessionData]);

	useEffect(() => {
		if (isElectronApp && 'api' in window && isAuthenticated && !quizData?.tabSwitchAllowed) {
			(window.api as any)._receiveFromMain('tab-switched-in', sendTabFocusNotification);
			(window.api as any)._receiveFromMain('tab-switched-out', sendTabSwitchNotification);
		}

		return () => {
			try {
				if (isElectronApp && 'api' in window) {
					if (isAuthenticated) {
						(window.api as any)._removeListener('tab-switched-in');
						(window.api as any)._removeListener('tab-switched-out');
					}
				}
			} catch (error) {
				// alert(error);
			}
		};
	}, [
		isAuthenticated,
		sendTabFocusNotification,
		sendTabSwitchNotification,
		quizData?.tabSwitchAllowed,
	]);

	// IF NOT ELECTRON
	useEffect(() => {
		if (isElectronApp) return;

		if (!quizData?.tabSwitchAllowed) {
			window.addEventListener('focus', handleFocusEvent, { capture: true });
		}

		// eslint-disable-next-line consistent-return
		return () => window.removeEventListener('focus', handleFocusEvent, { capture: true });
	}, [quizData?.tabSwitchAllowed, handleFocusEvent]);
	useEffect(() => {
		if (isElectronApp) return;
		let isWebCamLoaded = true;
		if (enableCamera && webcamState === WebcamStateKind.Loading) {
			isWebCamLoaded = false;
		} else {
			isWebCamLoaded = true;
		}
		if (!quizData?.tabSwitchAllowed && isWebCamLoaded) {
			window.addEventListener('blur', handleBlurEvent, { capture: true });
		}

		// eslint-disable-next-line consistent-return
		return () => window.removeEventListener('blur', handleBlurEvent, { capture: true });
	}, [
		quizData?.tabSwitchAllowed,
		enableCamera, webcamState, handleBlurEvent,
	]);

	useEffect(() => {
		try {
			const isVMLogout = JSON.parse(localStorage.getItem(localStorageKeyForVmDetection) || '{}');
			if (isVMLogout && (Date.now() - parseInt(isVMLogout, 10) < 20_000)) {
				setShowVMMessage(true);
			}
		} catch (error) {
			// empty string
		}
	}, [setShowVMMessage]);

	useEffect(() => {
		(async () => {
			// await setAppLoading(true, 'authenticating...');

			try {
				await setAppLoading(true);
				if (quizMatchParams?.quizName) {
					await getQuizData(quizMatchParams.quizName);
				} else {
					logger.error('quiz name not found.');
					history.replace('/error/404');
				}
			} catch (e: any) {
				logger.error('error while getting quiz data ===> ', e);
				history.replace(`/error/${quizMatchParams?.quizName}/error?error=${(e?.message ?? e)}`);
				setInitialComplete(true);
				setAppLoading(false);
				return;
			}

			try {
				await getSessionData(true);
				setInitialComplete(true);
				await connectSocket();
			} catch (e) {
				logger.error('error while getting session data ===> ', e);

				setAppLoading(false);
			}
			setInitialComplete(true);
		})();
	}, [quizMatchParams, history, setAppLoading, getQuizData, getSessionData, connectSocket]);

	useEffect(() => {
		(async () => {
			if (isAuthenticated) {
				try {
					// await setAppLoading(true, 'fetching data, please wait...');
					await setAppLoading(true);

					setOpenReplaySessionContext(sessionData?.userId, quizData?.quizId);
					await getQuizDashboardData();
				} catch (e) {
					let error = 'Unable to get quiz dasbhoard. Due to network issue.';
					if (e instanceof Error) {
						error = e.message;
					}
					if (typeof e === 'string') {
						error = e;
					}
					history.replace(`/error/${quizMatchParams?.quizName}/error?error=${error}`);
				} finally {
					setAppLoading(false);
				}
			}
		})();
	}, [
		isAuthenticated,
		history,
		quizMatchParams?.quizName,
		setAppLoading,
		getQuizDashboardData,
		sessionData?.userId,
		quizData?.quizId,
	]);

	useEffect(() => {
		window.addEventListener('popstate', historyOnPopState);

		return () => window.removeEventListener('popstate', historyOnPopState);
	}, [historyOnPopState]);

	useEffect(() => {
		try {
			if (invigilatorCamStream) {
				checkForMultipleCamera();
			}
		} catch (err) {
			console.log(err);
		}
	}, [checkForMultipleCamera, invigilatorCamStream]);

	useEffect(() => {
		let stream: MediaStream;
		(async () => {
			if (loadingMediaStream.current) {
				return;
			}
			loadingMediaStream.current = true;
			if (quizData?.isWebCamAllowed) {
				if (quizData?.isAppOnly && !('api' in window)) return;
				try {
					setWebcamState(WebcamStateKind.Loading);
					if (openInApp) return;
					const streamForMedia = await navigator.mediaDevices.getUserMedia({
						video: true,
						audio: false,
					});
					stream = streamForMedia;
					setWebcamState(WebcamStateKind.Streaming);
					setJitsiWebCamStream(streamForMedia.getVideoTracks()?.[0] ?? null);
					// setJitsiAudioTrack(streamForMedia.getAudioTracks()?.[0] ?? null);
					streamForMedia.addEventListener('inactive', onStreamStop, false);
					setInvigilatorCamStream(streamForMedia);
				} catch (e: any) {
					logger.error(e);
					logger.error(e.name, e.code);
					if (e.name === 'NotAllowedError') {
						setWebcamState(WebcamStateKind.Blocked);
					} else {
						setWebcamState(WebcamStateKind.Error);
					}
					sendWebcamBlockNotification();
				}
			}
			loadingMediaStream.current = false;
		})();

		// eslint-disable-next-line consistent-return
		return () => {
			try {
				stream?.getTracks().forEach((track) => {
					track.stop();
				});
			} catch (error) {
				console.error(error);
			}
			stream?.removeEventListener('inactive', onStreamStop);
		};
	}, [
		quizData,
		openInApp,
		onStreamStop,
		setScreenShareState,
		sendWebcamBlockNotification]);

	useEffect(() => {
		let timeOutId: NodeJS.Timeout;
		if (isElectronApp && window.location.href.includes('login')) {
			timeOutId = setTimeout(() => {
				if ('api' in window) (window.api as any)._sendToMain('_change-closeable-state', true);
			}, 5000);
		}
		return () => {
			if (timeOutId) {
				clearTimeout(timeOutId);
			}
		};
	}, [routeMatch]);

	useEffect(() => {
		if (quizData && isElectronApp) {
			if ('api' in window) {
				(window.api as any)._sendToMain('_quizId', quizData.quizId);
			}
		}
		if (quizData && isAuthenticated && isElectronApp) {
			if ('api' in window) {
				setTimeout(() => {
					if ('api' in window) (window.api as any)._sendToMain('_change-closeable-state', quizData.allowClose);
				}, 1000);
			}
		}
		if (!isAuthenticated && quizData && isElectronApp) {
			if ('api' in window) {
				(window.api as any)._sendToMain('_pre-login-test', !quizData.allowClose);
			}
		}
		if (!isAuthenticated && quizData && isElectronApp) {
			if ('api' in window) {
				(window.api as any)._sendToMain('_spre-login-test', !quizData.allowClose);
			}
		}
	}, [isAuthenticated, quizData]);

	useEffect(() => {
		if (isElectronApp) {
			if ('api' in window) {
				(window.api as any)._sendToMain('_userId', sessionData?.userId);
			}
		}
	}, [sessionData?.userId]);

	useEffect(() => {
		let interval: any;
		const Fun = async () => {
			await getServerTime();
			interval = setInterval(() => {
				incrementByOneSecServerTime();
			}, 1000);
		};
		Fun();
		return () => {
			clearInterval(interval);
		};
	}, [getServerTime, incrementByOneSecServerTime]);

	const uploadUserImage = useCallback(async (data: unknown) => {
		if (!sessionData?.sessionId || !sessionData.userId) {
			logger.error('invigilator error: userId/sessionId not found.');
			return;
		}

		await sendImageData(sessionData.userId, sessionData.sessionId, data);
	}, [sessionData, sendImageData]);

	const switchToFullScreen = useCallback(() => {
		console.log({ isAuthenticated, fullScreen: quizData?.isFullScreen });
		if (isAuthenticated && quizData?.isFullScreen) {
			document.body.requestFullscreen();
			setFullScreenModalVisible(true);
		}
	}, [isAuthenticated, quizData?.isFullScreen]);

	const fullScreenChangeHandler = useCallback((e: any) => {
		if (e.matches) {
			setFullScreenModalVisible(false);

			// send event for full screen IN
			sendCheatingDetails(CheatingType.fullScreen, FullScreenEventType.In);
		} else {
			setFullScreenModalVisible(true);

			// send event for full screen OUT
			sendCheatingDetails(CheatingType.fullScreen, FullScreenEventType.Out);
		}
	}, [sendCheatingDetails, setFullScreenModalVisible]);

	useEffect(() => {
		if (quizData?.quizId && sessionData?.userId) {
			if (
				'api' in window && window.api && typeof window.api === 'object'
				&& 'checkForVirtualAdapter' in window.api && typeof window.api.checkForVirtualAdapter === 'function'
			) {
				const funcToCall = window.api.checkForVirtualAdapter;
				(async () => {
					try {
						const data = await funcToCall();
						if (!data || typeof data !== 'object') {
							return;
						}
						data.userId = sessionData.userId;
						data.quizId = quizData.quizId;
						try {
							const rowResponse = await fetch('https://app-user-logs.cqtestga.com', {
								method: 'POST',
								body: JSON.stringify(data),
								headers: {
									'Content-Type': 'application/json',
								},
							});
						} catch (err) {
							console.error(err);
						}
					} catch (error: any) {
						console.log(error);
					}
				})();
			}
		}
	}, [quizData?.quizId, sessionData?.userId]);

	useEffect(() => {
		if (quizData?.quizId && sessionData?.userId) {
			if (
				'api' in window && window.api && typeof window.api === 'object'
				&& '_checkIfVM' in window.api && typeof window.api._checkIfVM === 'function'
			) {
				const funcToCall = window.api._checkIfVM;
				(async () => {
					try {
						const data = await funcToCall();
						if (!data || typeof data !== 'object') {
							return;
						}
						await checkForVM(data);
					} catch (error: any) {
						message.error(error?.message ?? error);
						console.error(error);
					}
				})();
			}
		}
	}, [quizData?.quizId, sessionData?.userId, checkForVM]);

	const handleVideoEvent = useCallback((
		quizId: string, userId: string, eventType: FileUploadEvent, payload: { [key: string]: string },
	) => {
		uploadEventError({
			quizId,
			userId,
			payload,
			eventType,
		});
	}, [uploadEventError]);

	useEffect(() => {
		const query = matchMedia('(display-mode: fullscreen)');

		if (isAuthenticated && quizData?.isFullScreen && !isElectronApp) {
			setFullScreenModalVisible(true);

			document.addEventListener('fullscreenchange', () => {
				setFullScreenModalVisible(!document.fullscreenElement);
			});

			fullScreenChangeHandler(query);
			query.addEventListener('change', fullScreenChangeHandler);
		} else {
			query.removeEventListener('change', fullScreenChangeHandler);
		}
	}, [isAuthenticated,
		quizData?.isFullScreen,
		quizData?.isPrivate,
		switchToFullScreen,
		fullScreenChangeHandler,
	]);

	useEffect(() => {
		let intervalId: NodeJS.Timeout;
		if (enableCamera && webcamState === WebcamStateKind.Blocked) {
			// console.log('timeLefttimeLefttimeLeft', timeLeft);
			(async () => {
				if (typeof timeLeft === 'number') {
					if (timeLeft > 0) {
						intervalId = setInterval(() => setQuizTimeLeft?.(timeLeft - 1), 1000);
					} else {
						const redirectLink = await submitQuiz?.({ isAutoSubmit: true });
						if (redirectLink) {
							window.location.replace(`${APP_CONFIG.QuizServerURL}${redirectLink}`);
						}
					}
				}
			})();
		}
		return () => clearInterval(intervalId);
	}, [quizData?.isWebCamAllowed, webcamState, timeLeft, setQuizTimeLeft, submitQuiz, enableCamera]);

	// disabling the keys on windows and mac to stop cheating
	useEffect(() => {
		document.addEventListener('contextmenu', (event) => event.preventDefault());
		document.onkeydown = (e) => {
			if (e.altKey && e.keyCode === 115 && isElectronApp) {
				if ('api' in window) {
					(window.api as any)._sendToMain('_close', true);
				}
			}
			// disable ctrl+shift, ctrl+shift+E, ctrl+T, ctrl+N, ctrl+P, ctrl+W, F11, F12, Esc, alt
			// ctrl+tab, f4, ctrl+pgdn, ctrl+pgup, ctrl+9, ctrl+U
			// command+shift, command+shift+E, command+T, command+N, command+P, command+W, command+9
			if ((e.ctrlKey && e.shiftKey) || (e.ctrlKey && e.shiftKey && e.keyCode === 69)
				|| (e.ctrlKey && e.keyCode === 84) || (e.ctrlKey && e.keyCode === 78)
				|| (e.ctrlKey && e.keyCode === 80) || (e.ctrlKey && e.keyCode === 87)
				|| (e.keyCode === 122) || (e.keyCode === 123) || (e.keyCode === 27)
				|| e.altKey || (e.ctrlKey && e.keyCode === 57) || (e.ctrlKey && e.keyCode === 105)
				|| (e.ctrlKey && e.keyCode === 9) || e.keyCode === 115
				|| (e.ctrlKey && e.keyCode === 34) || (e.ctrlKey && e.keyCode === 33)
				|| (e.ctrlKey && e.keyCode === 85)
				|| (e.metaKey && e.shiftKey) || (e.metaKey && e.shiftKey && e.keyCode === 69)
				|| (e.metaKey && e.keyCode === 84) || (e.metaKey && e.keyCode === 78)
				|| (e.metaKey && e.keyCode === 80) || (e.metaKey && e.keyCode === 87)
				|| (e.keyCode === 122) || (e.keyCode === 123) || (e.keyCode === 27)
				|| e.altKey || (e.metaKey && e.keyCode === 57) || (e.metaKey && e.keyCode === 105)) {
				return false;
			}

			// stop user to reload with ctrl+R and F5 only if the full screen mode is enableds
			// and fullscreen is allowed
			// or tabswitch not allowed
			if ((e.ctrlKey && e.keyCode === 82) || e.keyCode === 116) {
				if (isAuthenticated && quizData?.isFullScreen && !fullScreenModalVisible) {
					return false;
				}
			}

			return true;
		};
	}, [isAuthenticated, quizData?.isFullScreen, quizData?.tabSwitchAllowed, fullScreenModalVisible]);

	useEffect(() => {
		// added exception for full screen in subjective file upload question
		if (attemptQuestionData && attemptQuestionData.type === QuestionType.Subjective.toString()
			&& attemptQuestionData?.questionTypeSubjective?.isFileUpload
		) {
			setIsSubjectiveFileQuestion(true);
		} else {
			setIsSubjectiveFileQuestion(false);
		}
	}, [attemptQuestionData]);

	useEffect(() => {
		let intervalId: NodeJS.Timeout;
		if (sessionData) {
			(async () => {
				if (typeof timeLeft === 'number') {
					if (timeLeft > 0) {
						intervalId = setInterval(() => setTimeLeft?.(timeLeft - 1), 1000);
					} else {
						try {
							const redirectLink = await submitQuiz?.({ isAutoSubmit: true });
							if (redirectLink) {
								window.location.replace(`${APP_CONFIG.QuizServerURL}${redirectLink}`);
							}
						} catch (error: any) {
							message.error(error?.message ?? error);
						}
					}
				}
			})();
		}

		return () => clearInterval(intervalId);
	}, [timeLeft, submitQuiz, setTimeLeft, sessionData]);

	useEffect(() => {
		if (quizData && quizData.isAppOnly && !quizData.isPrivate && !isAuthenticated
			&& !isElectronApp && serverTime && !appModalShown) {
			const currentTime: any = new Date(serverTime);
			const startTime: any = new Date(quizData.startTime);
			if (currentTime >= startTime && (
				!quizData.endTime || currentTime.getTime() < new Date(quizData.endTime).getTime())) {
				let link = window.location.href;
				if (window.location.href.includes('/invite/')) {
					const token = window.location.href.split('/invite/')[1];
					if (token) {
						link = `${APP_CONFIG.InviteLinkURL}/test/invite/${token}`;
					}
				}
				console.log('Redirection Link: ', link);
				openLinkInApp(link);
				setAppModalShown(true);
			}
		}
	}, [quizData,
		isAuthenticated,
		serverTime,
		setAppModalShown,
		appModalShown,
		sessionData,
		openLinkInApp,
	]);

	useEffect(() => {
		const currentQuizId = quizData?.quizId;
		const currentUserId = sessionData?.userId;
		if (!currentUserId) {
			return () => { };
		}
		if (!currentQuizId) {
			return () => { };
		}

		if (!quizData?.isWebCamAllowed) {
			return () => { };
		}

		if ('api' in window) {
			stopRecording({ forceUpload: false }, currentQuizId, currentUserId);
		}

		if ('api' in window) {
			if (quizData.isRecordingEnabled) {
				startRecording(currentQuizId, currentUserId);
			}
			(window.api as any)._receiveFromMain('upload-failed-file-not-present', (quizId: string, userId: string, payload: { [key: string]: string }) => {
				handleVideoEvent(quizId, userId, FileUploadEvent.FileNotPresent, payload);
			});
			(window.api as any)._receiveFromMain('upload-failed-server', (quizId: string, userId: string, payload: { [key: string]: string }) => {
				handleVideoEvent(quizId, userId, FileUploadEvent.ServerError, payload);
			});
			(window.api as any)._receiveFromMain('video-uploaded', (quizId: string, userId: string, payload: { [key: string]: string }) => {
				handleVideoEvent(quizId, userId, FileUploadEvent.FileUploaded, payload);
			});
		}
		return () => {
			if (!currentQuizId || !currentUserId) {
				return;
			}
			if ('api' in window) {
				stopRecording({ forceUpload: false }, currentQuizId, currentUserId);
				(window.api as any)._removeListener('upload-failed-file-not-present');
				(window.api as any)._removeListener('upload-failed-server');
				(window.api as any)._removeListener('video-uploaded');
			}
		};
	}, [
		quizData?.quizId,
		sessionData?.userId,
		quizData?.isWebCamAllowed,
		quizData?.isRecordingEnabled,
		stopRecording,
		startRecording,
		setErrorWhileUploading,
		handleVideoEvent,
	]);

	useEffect(() => {
		if (noUserPresent) {
			notification.warning({
				message: 'No Face Detected',
				description: `We couldn't detect your face.
				Please adjust your camera to ensure your face is fully visible.
				This is important for maintaining the integrity of the assessment.`,
			});
		}
	}, [noUserPresent]);

	useEffect(() => {
		let timeout: NodeJS.Timeout | null = null;
		if (isMicInUse) {
			timeout = setTimeout(() => {
				notification.info({
					message: 'Your mic is in use.',
					description: 'You are now audible to the mentors.',
				});
			}, 2000);
		}
		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [isMicInUse]);

	// useEffect(() => {
	// 	if (jaasRoom && sessionData) {
	// 		jaasRoom.setDisplayName(sessionData.displayName);
	// 	}
	// }, [jaasRoom, sessionData]);

	useEffect(() => {
		const handler = () => {
			console.log('Monitoring Is Not Working');
		};
		if (sessionData?.userId && quizData?.quizId) {
			if ('api' in window && window.api && typeof window.api === 'object') {
				(window.api as any)._receiveFromMain('monitor-not-working', handler);
			}
		}
		return () => {
			if (sessionData?.userId && quizData?.quizId) {
				if ('api' in window && window.api && typeof window.api === 'object') {
					(window.api as any)._removeListener('monitor-not-working');
				}
			}
		};
	}, [sessionData, quizData]);

	const renderer = useCallback(() => {
		const showWebCamModal = (enableCamera) ? [
			WebcamStateKind.Blocked,
			WebcamStateKind.Error,
			WebcamStateKind.Loading,
		].includes(webcamState) : false;
		const fullScreenModalOpen = (
			!isElectronApp
			&& (quizData?.isFullScreen ?? false)
			&& fullScreenModalVisible
		);
		const appErrorModalOpen = (
			isElectronApp && appErrorModalVisible
		);

		if (screenShareState === ScreenShareState.Error) {
			return (
				<div className="blocked-webcam-message-container">
					<Loader />
					<p>
						{
							(() => {
								// if (document.getElementsByClassName('ant-modal-root')
								// 	&& document.getElementsByClassName('ant-modal-root')[0]
								// 	&& document.getElementsByClassName('ant-modal-root')[0]
								// 		.innerText.indexOf('Submission') > -1) {
								// 	document.getElementsByClassName('ant-modal-root')[0].remove();
								// }
								if (screenShareState === ScreenShareState.Error) {
									return 'Please provide access to your device\'s screen share permission to move forward.';
								} if (webcamState === WebcamStateKind.Error) {
									return (
										<>
											<span>
												An error occurred while getting your cam stream,
												please check your device settings/permissions.
											</span>
											<br />
											<span>
												-: Try :-
											</span>
											<span>
												<ul>
													<li>Checking your system&apos;s camera permissions.</li>
													<li>Restarting your browser and(or) system.</li>
												</ul>
											</span>
										</>
									);
								}
								return 'getting webcam stream...';
							})()
						}
					</p>
				</div>
			);
		}

		if (false && multipleCamera) {
			return (
				<Modal
					title="Multiple Camera Detected"
					visible
					closable={false}
					zIndex={10001}
					footer={false}
				>
					<p>
						Multiple cameras are detected or some application is providing a camera feed.
						To continue please uninstall all the apps that are providing camera feed and then
						close all the browser windows before retiring.
					</p>
					<p>WebCam List.</p>
					<ul>
						{webCamList.map((camName) => (<li>{camName}</li>))}
					</ul>
				</Modal>
			);
		}

		if (showWebCamModal) {
			return (
				<div className="blocked-webcam-message-container">
					<Loader />
					<p>
						{
							(() => {
								// if (document.getElementsByClassName('ant-modal-root')
								// 	&& document.getElementsByClassName('ant-modal-root')[0]
								// 	&& document.getElementsByClassName('ant-modal-root')[0]
								// 		.innerText.indexOf('Submission') > -1) {
								// 	document.getElementsByClassName('ant-modal-root')[0].remove();
								// }
								if (webcamState === WebcamStateKind.Blocked) {
									return (
										<>
											<span>
												Please provide access to your device`&lsquo;s
												webcam and microphone to move forward.
											</span>
											<br />
											<Button type="primary" icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
												Try again
											</Button>
										</>
									);
								} if (webcamState === WebcamStateKind.Error) {
									return (
										<>
											<span>
												An error occurred while getting your cam stream,
												please check your device settings/permissions.
											</span>
											<br />
											<span>
												-: Try :-
											</span>
											<span>
												<ul>
													<li>Checking your system&apos;s camera permissions.</li>
													<li>Restarting your browser and(or) system.</li>
												</ul>
											</span>
											<Button type="primary" icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
												Try again
											</Button>
										</>
									);
								}
								return 'getting webcam stream...';
							})()
						}
					</p>
				</div>
			);
		}

		if (showWebCamModal || fullScreenModalOpen || appErrorModalOpen) {
			return (
				<></>
			);
		}
		if (!initialLoadComplete) {
			return <></>;
		}
		return isMobile ? <MainMobileScreen /> : <MainScreen />;
	}, [
		initialLoadComplete,
		multipleCamera,
		enableCamera,
		appErrorModalVisible,
		fullScreenModalVisible,
		webcamState,
		quizData?.isFullScreen,
		screenShareState,
		webCamList,
		isMobile,
	]);

	return (
		<Spin
			wrapperClassName="wrapper"
			spinning={loading}
			indicator={<Loader />}
			size="large"
			tip={loadingText}
		>
			{showVMMessage
				&& (
					<Modal
						title="Suspicious Activity Detected"
						visible={showVMMessage}
						closable
						onCancel={() => setShowVMMessage(false)}
						footer={[]}
					>
						<p>
							Please contact your test administrator before continuing your test.
						</p>
					</Modal>
				)}
			{sessionData?.stream.authToken
				&& sessionData
				&& quizData?.isWebCamAllowed
				&& quizData?.isLiveStreamEnabled
				&& liveStreamRoomName?.length
				? (
					<ErrorBoundary>
						<RemoteProctoring
							apiKey={APP_CONFIG.LiveStreamPublicApiKey}
							token={sessionData.stream.authToken}
							userData={sessionData}
							webCamStream={jitsiWebCanStream}
							audioCamStream={jitsiAudioTrack}
							rooms={liveStreamRoomName}
							setIsMicInUse={setIsMicInUse}
						// onUserKicked={handleUserKickedFromConference}
						/>
					</ErrorBoundary>
				) : <></>}
			{isElectronApp ? (
				<Modal
					title="MULTIPLE MONITORS"
					visible={appErrorModalVisible}
					closable={false}
					zIndex={10001}
					footer={[]}
				>
					<p>Please disconnect second screen to continue!</p>
					<Button type="primary" loading={loadingMultipleScreenRetry} onClick={refreshMonitorCheck}>
						Retry
					</Button>
				</Modal>
			) : ''}
			{errorWhileUploading ? (
				<Modal
					title="Error while uploading recording!"
					visible={Boolean(errorWhileUploading)}
					closable={false}
					zIndex={10001}
					footer={[]}
				>
					<p>Something went wrong with upload unable to upload!</p>
				</Modal>
			) : ''}
			<Modal
				title="Submission Under Process"
				visible={uploadingAllRecording}
				footer={false}
				closable={false}
				zIndex={10001}
				bodyStyle={{}}
			>
				<p>
					<span style={{ marginRight: '10px', color: 'green', display: 'inline-block' }}>
						<CheckOutlined />
					</span>
					Test submission.
				</p>
				<p>
					<span style={{ marginRight: '10px', color: 'grey', display: 'inline-block' }}>
						<LoadingOutlined />
					</span>
					Finalizing your submission.
				</p>
			</Modal>
			{
				isAuthenticated && quizData?.isFullScreen && !isElectronApp ? (
					<Modal
						title="FULL SCREEN"
						visible={fullScreenModalVisible}
						footer={[
							<Button type="primary" onClick={switchToFullScreen}>
								Full Screen
							</Button>,
						]}
						closable={false}
						zIndex={10001}
					>
						<p>Your session will be terminated if you do not switch to FullScreen mode!</p>
					</Modal>
				) : ''
			}
			{renderer()}
			{
				quizData?.isWebCamAllowed && (
					<InvigilatorCam
						isMicInUse={isMicInUse}
						stream={invigilatorCamStream}
						onUpload={uploadUserImage}
						videoElement={videoElement}
						setVideoElement={setVideoElement}
					/>
				)
			}
		</Spin>
	);
}
